import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { searchJgsStudents as endUserSearch, linkMyJgsProfile } from "@/services/endUser";
import { updateMyProfile } from "@/services/profile";
import {
  X,
  ChevronDown,
  ChevronUp,
  UserRound,
  Trophy,
  Check,
  Loader2,
  Search,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  trackOnboardingProfileSaved,
  trackJgsProfileLinked,
  trackJgsProfileSkipped,
  trackJgsSearchInitiated,
  trackJgsSearchResults,
  trackJgsSearchError,
  trackJgsProfileLinkFailed,
  trackOnboardingCompleted,
} from "@/analytics/analytics";
import { useEndUser } from "@/contexts/EndUserContext";
import { US_STATES, CA_PROVINCES } from "./onboarding/locations";

interface JgsLinkBannerProps {
  onLinkSuccess: (profileId: string) => void | Promise<void>;
  variant?: "full" | "compact";
  onDismiss?: () => void;
  onComplete?: () => void;
  /** Called when the onboarding flow reaches a terminal state (linked or skipped) */
  onFlowDone?: () => void;
}

interface ProfileData {
  displayName: string;
  state: string;
}

interface FormValues {
  displayName: string;
  gradYear: string;
  homeState: string;
  gender: string;
}

// ── Combined profile + academic info section ────────────────────────────────

function ProfileSection({
  onSaved,
  initialValues,
}: {
  onSaved: (profileData: ProfileData, jgsMatches: any[], formValues: FormValues) => void;
  initialValues?: FormValues;
}) {
  const [displayName, setDisplayName] = useState(initialValues?.displayName ?? "");
  const [gradYear, setGradYear] = useState(initialValues?.gradYear ?? "");
  const [homeState, setHomeState] = useState(initialValues?.homeState ?? "");
  const [gender, setGender] = useState(initialValues?.gender ?? "");
  const [saving, setSaving] = useState(false);
  const [searchingJgs, setSearchingJgs] = useState(false);
  const [errors, setErrors] = useState<{
    displayName?: string;
    gradYear?: string;
    homeState?: string;
    gender?: string;
  }>({});

  const handleGradYearChange = (value: string) => {
    setGradYear(value.replace(/\D/g, "").slice(0, 4));
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (displayName.trim().length < 2) {
      newErrors.displayName = "Name must be at least 2 characters";
    }

    const yr = Number(gradYear);
    if (gradYear.length !== 4 || !Number.isInteger(yr) || yr < 2024 || yr > 2035) {
      newErrors.gradYear = "Enter a valid year (2024\u20132035)";
    }

    if (!homeState) {
      newErrors.homeState = "Please select your state";
    }

    if (!gender) {
      newErrors.gender = "Please select gender";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload: Record<string, unknown> = {
      displayName: displayName.trim(),
      gradYear: Number(gradYear),
      state: homeState,
      gender,
    };

    try {
      setSaving(true);
      await updateMyProfile(payload as any);
      trackOnboardingProfileSaved({ hasGpa: !!payload.gpa, hasSat: !!payload.satScore });
      toast.success("Profile saved!");
    } catch (err: any) {
      toast.error(String(err?.message || "Failed to save"));
      setSaving(false);
      return;
    }

    // Auto-search JGS
    setSaving(false);
    setSearchingJgs(true);
    trackJgsSearchInitiated({ source: "onboarding", query: displayName.trim() });
    try {
      const res = await endUserSearch(displayName.trim(), homeState, 10, gender);
      const matches = Array.isArray(res) ? res : [];
      trackJgsSearchResults({
        source: "onboarding",
        query: displayName.trim(),
        resultCount: matches.length,
      });
      onSaved({ displayName: displayName.trim(), state: homeState }, matches, {
        displayName,
        gradYear,
        homeState,
        gender,
      });
    } catch {
      // Search failed — proceed with empty results so user can manual search
      trackJgsSearchError({ source: "onboarding", query: displayName.trim() });
      onSaved({ displayName: displayName.trim(), state: homeState }, [], {
        displayName,
        gradYear,
        homeState,
        gender,
      });
    } finally {
      setSearchingJgs(false);
    }
  };

  if (searchingJgs) {
    return (
      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary-text" />
          <span className="text-sm text-muted-foreground">Searching for your JGS profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
      <div className="flex items-center gap-2 mb-1">
        <UserRound className="h-4 w-4 text-primary-text shrink-0" />
        <h3 className="text-sm font-semibold text-foreground">Let's get started</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        A few quick details so we can personalize your recruiting advice and find programs that fit.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Golfer's name <span className="text-destructive">*</span>
          </label>
          <Input
            placeholder="Golfer's first and last name"
            value={displayName}
            onChange={e => {
              setDisplayName(e.target.value);
              setErrors(p => ({ ...p, displayName: undefined }));
            }}
          />
          {errors.displayName && (
            <p className="text-xs text-destructive mt-1">{errors.displayName}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Graduation year <span className="text-destructive">*</span>
          </label>
          <Input
            placeholder="e.g. 2028"
            value={gradYear}
            onChange={e => {
              handleGradYearChange(e.target.value);
              setErrors(p => ({ ...p, gradYear: undefined }));
            }}
            inputMode="numeric"
          />
          {errors.gradYear && <p className="text-xs text-destructive mt-1">{errors.gradYear}</p>}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Home state <span className="text-destructive">*</span>
          </label>
          <Select
            value={homeState || undefined}
            onValueChange={v => {
              setHomeState(v);
              setErrors(p => ({ ...p, homeState: undefined }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>United States</SelectLabel>
                {US_STATES.map(s => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>Canada</SelectLabel>
                {CA_PROVINCES.map(p => (
                  <SelectItem key={p.code} value={p.code}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {errors.homeState && <p className="text-xs text-destructive mt-1">{errors.homeState}</p>}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Gender <span className="text-destructive">*</span>
          </label>
          <Select
            value={gender || undefined}
            onValueChange={v => {
              setGender(v);
              setErrors(p => ({ ...p, gender: undefined }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
          {errors.gender && <p className="text-xs text-destructive mt-1">{errors.gender}</p>}
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={handleSave} disabled={saving} size="sm">
          {saving ? "Saving\u2026" : "Save and Continue"}
        </Button>
      </div>
    </div>
  );
}

// ── JGS search form ─────────────────────────────────────────────────────────

interface JgsSearchFormProps {
  onLinkSuccess: (profileId: string) => void | Promise<void>;
  source: "onboarding" | "profile" | "chat";
  initialName?: string;
  initialState?: string;
  initialGender?: string;
  onSkip?: () => void;
  /** Run the search automatically on mount when name/state are prefilled. */
  autoSearch?: boolean;
}

export function JgsSearchForm({
  onLinkSuccess,
  source,
  initialName,
  initialState,
  initialGender,
  onSkip,
  autoSearch,
}: JgsSearchFormProps) {
  const [searchName, setSearchName] = useState(initialName ?? "");
  const [searchState, setSearchState] = useState(initialState ?? "");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  // Present likely matches immediately when the wizard already collected
  // name + state in the basics step (avoid making the user re-search).
  useEffect(() => {
    if (autoSearch && (initialName ?? "").trim().length >= 2) {
      void handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    if (!searchName || searchName.trim().length < 2) {
      toast.info("Enter at least 2 characters for the name");
      return;
    }
    trackJgsSearchInitiated({ source, query: searchName.trim() });
    try {
      setSearching(true);
      const res = await endUserSearch(searchName, searchState || undefined, 10, initialGender);
      const results = Array.isArray(res) ? res : [];
      setMatches(results);
      setSelectedProfile(null);
      trackJgsSearchResults({ source, query: searchName.trim(), resultCount: results.length });
    } catch (err: any) {
      toast.error(String(err?.message || "Search failed"));
      setMatches([]);
      trackJgsSearchError({ source, query: searchName.trim() });
    } finally {
      setSearching(false);
      setHasSearched(true);
    }
  };

  const handleLink = async () => {
    if (!selectedProfile) return;
    try {
      setLinking(true);
      await linkMyJgsProfile(selectedProfile);
      trackJgsProfileLinked({ source, matchCount: matches.length });
      toast.success("Profile linked! Your AI coach now has access to your JGS stats.");
      await onLinkSuccess(selectedProfile);
    } catch (err: any) {
      toast.error(String(err?.message || "Linking failed"));
      trackJgsProfileLinkFailed({ source });
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Input
          placeholder="Player name"
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleSearch();
          }}
        />
        <Select value={searchState || undefined} onValueChange={v => setSearchState(v)}>
          <SelectTrigger className="max-w-[220px]">
            <SelectValue placeholder="State/Province" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>United States</SelectLabel>
              {US_STATES.map(s => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name} ({s.code})
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>Canada</SelectLabel>
              {CA_PROVINCES.map(p => (
                <SelectItem key={p.code} value={p.code}>
                  {p.name} ({p.code})
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Button onClick={handleSearch} disabled={searching}>
          {searching ? "Searching\u2026" : "Search"}
        </Button>
      </div>

      {hasSearched || matches.length > 0 ? (
        matches.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground mb-4">
            No results found. Try a different name or state.
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {matches.map((m: any, idx: number) => {
              const id = String(m.jgsProfile || `${m.player_name}-${idx}`);
              const isSelected = selectedProfile === id;
              return (
                <button
                  key={id}
                  onClick={() => setSelectedProfile(id)}
                  className={`w-full rounded-lg border-2 p-3 text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {m.player_name || "Unknown"}
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-primary-text shrink-0" />}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{m.state || "—"}</span>
                    <span>Class of {m.class_year || "—"}</span>
                    {typeof m.rank === "number" && <span>Rank #{m.rank.toLocaleString()}</span>}
                    {typeof m.scoring_differential !== "undefined" &&
                      m.scoring_differential !== null && <span>Diff {m.scoring_differential}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <p className="text-xs text-muted-foreground mb-4">
          Enter your name as it appears on Junior Golf Scoreboard and select your state to find your
          profile.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-end gap-2">
          <Button onClick={handleLink} disabled={!selectedProfile || linking}>
            {linking ? "Linking\u2026" : "This is me"}
          </Button>
        </div>
        {onSkip && (
          <p className="text-xs text-muted-foreground text-center w-full">
            No ranking yet? No problem —{" "}
            <button
              type="button"
              onClick={onSkip}
              className="underline underline-offset-2 hover:text-foreground transition-colors"
            >
              skip this step
            </button>{" "}
            and we&apos;ll help you get ranked as part of your college journey.
          </p>
        )}
      </div>
    </>
  );
}

// ── Results cards for auto-search matches ────────────────────────────────────

function JgsResultsCards({
  matches,
  onLink,
  source,
}: {
  matches: any[];
  onLink: (profileId: string) => void | Promise<void>;
  source: "onboarding" | "profile" | "chat";
}) {
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  const handleLink = async () => {
    if (!selectedProfile) return;
    try {
      setLinking(true);
      await linkMyJgsProfile(selectedProfile);
      trackJgsProfileLinked({ source, matchCount: matches.length });
      toast.success("Profile linked!");
      await onLink(selectedProfile);
    } catch (err: any) {
      toast.error(String(err?.message || "Linking failed"));
      trackJgsProfileLinkFailed({ source });
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <div className="space-y-2 mb-4">
        {matches.map((m: any, idx: number) => {
          const id = String(m.jgsProfile || `${m.player_name}-${idx}`);
          const isSelected = selectedProfile === id;
          return (
            <button
              key={id}
              onClick={() => setSelectedProfile(id)}
              className={`w-full rounded-lg border-2 p-3 text-left transition-colors ${
                isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">
                  {m.player_name || "Unknown"}
                </span>
                {isSelected && <Check className="h-4 w-4 text-primary-text shrink-0" />}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span>{m.state || "—"}</span>
                <span>Class of {m.class_year || "—"}</span>
                {typeof m.rank === "number" && <span>Rank #{m.rank.toLocaleString()}</span>}
                {typeof m.scoring_differential !== "undefined" &&
                  m.scoring_differential !== null && <span>Diff {m.scoring_differential}</span>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex justify-end">
        <Button onClick={handleLink} disabled={!selectedProfile || linking}>
          {linking ? "Linking\u2026" : "This is me"}
        </Button>
      </div>
    </>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

type OnboardingState = "form" | "choice" | "results";

export default function JgsLinkBanner({
  onLinkSuccess,
  variant = "full",
  onDismiss,
  onComplete,
  onFlowDone,
}: JgsLinkBannerProps) {
  const [expanded, setExpanded] = useState(false);

  // State machine for variant="full" onboarding flow
  const [flowState, setFlowState] = useState<OnboardingState>("form");
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [jgsMatches, setJgsMatches] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    displayName: "",
    gradYear: "",
    homeState: "",
    gender: "",
  });

  const navigate = useNavigate();
  const { refreshUserData } = useEndUser();

  const handleProfileSaved = (data: ProfileData, matches: any[], values: FormValues) => {
    setProfileData(data);
    setJgsMatches(matches);
    setShowSearch(false);
    setFormValues(values);
    // Refresh context so Home screen and header update immediately
    refreshUserData();

    // Always show the two-card choice — auto-search results are cached in jgsMatches
    setFlowState("choice");
  };

  const [didLink, setDidLink] = useState(false);

  const handleSkip = async () => {
    setDidLink(false);
    trackJgsProfileSkipped({ source: "onboarding" });
    await updateMyProfile({ onboardingComplete: true } as any).catch(() => {});
    trackOnboardingCompleted({ outcome: "skipped", destination: "chat" });
    onFlowDone?.();
    navigate("/chat", {
      state: {
        autoPrompt:
          "[ONBOARDING_COMPLETE:unranked] New user just completed onboarding. Welcome them and give a brief overview of how college golf recruiting works.",
        threadTitle: "Getting started",
        isOnboarding: true,
      },
    });
  };

  const handleLinkSuccess = async (profileId: string) => {
    setDidLink(true);
    trackJgsProfileLinked({ source: "onboarding", matchCount: jgsMatches.length });
    await onLinkSuccess(profileId);
    await updateMyProfile({ onboardingComplete: true } as any).catch(() => {});
    trackOnboardingCompleted({ outcome: "linked", destination: "chat" });
    onFlowDone?.();
    navigate("/chat", {
      state: {
        autoPrompt:
          "[ONBOARDING_COMPLETE:linked] New user just completed onboarding and linked their JGS profile. Welcome them and help them understand where they stand in the recruiting landscape.",
        threadTitle: "Getting started",
        isOnboarding: true,
      },
    });
  };

  if (variant === "compact") {
    return (
      <div className="mx-auto max-w-4xl px-4 pt-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">
                Have a JGS profile?{" "}
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-primary-text hover:underline font-medium inline-flex items-center gap-1"
                >
                  Link it for personalized school recommendations based on your real stats
                  {expanded ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>
              </p>
            </div>
            {onDismiss && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={onDismiss}
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {expanded && (
            <div className="mt-4">
              <JgsSearchForm onLinkSuccess={onLinkSuccess} source="chat" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // variant="full" — single adaptive onboarding flow
  const stepNum = flowState === "form" ? 1 : 2;

  return (
    <div className="space-y-4">
      {/* ── form: profile entry ── */}
      {flowState === "form" && (
        <ProfileSection onSaved={handleProfileSaved} initialValues={formValues} />
      )}

      {/* ── choice: two-card "Yes / No" JGS path ── */}
      {flowState === "choice" && (
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
          <button
            onClick={() => {
              setFlowState("form");
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <ArrowLeft className="h-3 w-3" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-4 w-4 text-primary-text shrink-0" />
            <h3 className="text-sm font-semibold text-foreground">Do you have a JGS ranking?</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Junior Golf Scoreboard tracks your tournament results and rankings. Linking your profile
            helps us recommend programs where you&apos;d be a competitive recruit.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => {
                setShowSearch(false);
                setFlowState("results");
              }}
              className="rounded-lg border-2 border-border p-4 cursor-pointer hover:border-primary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-1">
                <Search className="h-4 w-4 text-primary-text shrink-0" />
                <span className="text-sm font-semibold text-foreground">Yes, find my profile</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Search Junior Golf Scoreboard and link your ranking to your account.
              </p>
            </button>
            <button
              onClick={handleSkip}
              className="rounded-lg border-2 border-border p-4 cursor-pointer hover:border-primary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-1">
                <ArrowRight className="h-4 w-4 text-primary-text shrink-0" />
                <span className="text-sm font-semibold text-foreground">No, not yet</span>
              </div>
              <p className="text-xs text-muted-foreground">
                No problem — we&apos;ll help you get ranked as part of your college journey.
              </p>
            </button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-3">
            Not sure? Try searching — we&apos;ll check for you, and you can always skip later.
          </p>
        </div>
      )}

      {/* ── results: show JGS matches or manual search ── */}
      {flowState === "results" && (
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
          <button
            onClick={() => {
              setFlowState("choice");
              setShowSearch(false);
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <ArrowLeft className="h-3 w-3" />
            <span>Back</span>
          </button>

          {jgsMatches.length > 0 && !showSearch ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-4 w-4 text-primary-text shrink-0" />
                <h3 className="text-sm font-semibold text-foreground">
                  {jgsMatches.length === 1
                    ? "We found a possible match"
                    : `We found ${jgsMatches.length} possible matches`}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Select your Junior Golf Scoreboard profile to link your ranking, scores, and
                tournament history.
              </p>
              <JgsResultsCards
                matches={jgsMatches}
                onLink={handleLinkSuccess}
                source="onboarding"
              />
              <div className="flex flex-col items-center gap-2 mt-4">
                <Button variant="outline" onClick={handleSkip} className="w-full sm:w-auto">
                  I don&apos;t have a junior golf ranking yet
                </Button>
                <button
                  onClick={() => setShowSearch(true)}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors"
                >
                  {jgsMatches.length === 1
                    ? "That's not me — search manually"
                    : "None of these — search manually"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Search className="h-4 w-4 text-primary-text shrink-0" />
                <h3 className="text-sm font-semibold text-foreground">
                  Search for your JGS profile
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Try searching with your name as it appears on Junior Golf Scoreboard.
              </p>
              <JgsSearchForm
                onLinkSuccess={handleLinkSuccess}
                source="onboarding"
                initialName={profileData?.displayName}
                initialState={profileData?.state}
                onSkip={handleSkip}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
